import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Search, ShoppingBag, Sparkles } from 'lucide-react';
import { ROUTES } from '../../lib/routes';

const slides = [
  {
    id: 1,
    headline: 'Fresh. Local. Organic.',
    subheadline: 'Pure produce from Nepali farms, delivered fresh to your home.',
    primaryCTA: 'Shop Now',
    secondaryCTA: 'Explore Products',
    primaryHref: ROUTES.PRODUCTS,
    secondaryHref: ROUTES.PRODUCTS,
    backgroundImage: 'https://images.pexels.com/photos/2132171/pexels-photo-2132171.jpeg',
    badge: '🌿 Certified Organic',
  },
  {
    id: 2,
    headline: 'Supporting Nepali Farmers',
    subheadline: 'Every purchase uplifts local farming families.',
    primaryCTA: 'Meet the Farmers',
    secondaryCTA: 'Our Story',
    primaryHref: ROUTES.ABOUT,
    secondaryHref: ROUTES.ABOUT,
    backgroundImage: 'https://images.pexels.com/photos/2933243/pexels-photo-2933243.jpeg',
    badge: '👨‍🌾 Trusted by 200+ families',
  },
  {
    id: 3,
    headline: 'Sustainable Living Starts Here',
    subheadline: 'Choose clean food for a cleaner planet.',
    primaryCTA: 'Explore Products',
    secondaryCTA: 'Learn More',
    primaryHref: ROUTES.PRODUCTS,
    secondaryHref: ROUTES.ABOUT,
    backgroundImage: 'https://images.pexels.com/photos/1300957/pexels-photo-1300957.jpeg',
    badge: '🌍 Eco-Friendly',
  },
];

export const Hero: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000); // Auto-slide every 6 seconds

    return () => clearInterval(timer);
  }, []);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`${ROUTES.PRODUCTS}?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <section className="relative w-full h-[calc(100vh-5rem)] min-h-[600px] max-h-[800px] overflow-hidden">
      {/* Background Slides */}
      <div className="relative w-full h-full">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* Background Image with Overlay */}
            <div className="absolute inset-0">
              <img
                src={slide.backgroundImage}
                alt={slide.headline}
                className="w-full h-full object-cover"
              />
              {/* Green overlay with gradient matching logo aesthetic */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#2E7D32]/70 via-[#2E7D32]/50 to-[#2E7D32]/30" />
              {/* Additional mint green tint from logo background */}
              <div className="absolute inset-0 bg-[#E8F5E9]/5" />
            </div>

            {/* Content Overlay */}
            <div className="relative h-full flex items-center">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <div className="max-w-3xl">
                  {/* Badge */}
                  <div
                    className={`inline-flex items-center gap-2 px-4 py-2 bg-[#E8F5E9]/30 backdrop-blur-md border border-[#E8F5E9]/50 rounded-full text-white text-sm font-medium mb-6 transition-all duration-700 ${
                      index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                    }`}
                    style={{ transitionDelay: '200ms' }}
                  >
                    <Sparkles className="w-4 h-4" />
                    {slide.badge}
                  </div>

                  {/* Headline */}
                  <h1
                    className={`text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight transition-all duration-700 ${
                      index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                    }`}
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      textShadow: '2px 4px 12px rgba(0,0,0,0.4)',
                      transitionDelay: '300ms'
                    }}
                  >
                    {slide.headline}
                  </h1>

                  {/* Subheadline */}
                  <p
                    className={`text-xl md:text-2xl text-white/95 mb-10 leading-relaxed transition-all duration-700 ${
                      index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                    }`}
                    style={{
                      textShadow: '1px 2px 8px rgba(0,0,0,0.3)',
                      transitionDelay: '400ms'
                    }}
                  >
                    {slide.subheadline}
                  </p>

                  {/* CTA Buttons */}
                  <div
                    className={`flex flex-wrap gap-4 transition-all duration-700 ${
                      index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                    }`}
                    style={{ transitionDelay: '500ms' }}
                  >
                    <Link
                      to={slide.primaryHref}
                      className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#2E7D32] rounded-full font-semibold text-lg shadow-lg hover:shadow-xl hover:bg-[#E8F5E9] transform hover:scale-105 transition-all duration-300"
                    >
                      <ShoppingBag className="w-5 h-5" />
                      {slide.primaryCTA}
                    </Link>
                    <Link
                      to={slide.secondaryHref}
                      className="inline-flex items-center gap-2 px-8 py-4 bg-[#E8F5E9]/20 backdrop-blur-md border-2 border-[#E8F5E9] text-white rounded-full font-semibold text-lg hover:bg-[#E8F5E9] hover:text-[#2E7D32] transform hover:scale-105 transition-all duration-300"
                    >
                      {slide.secondaryCTA}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Search Bar - Centered at Bottom */}
      <div className="absolute bottom-32 left-1/2 -translate-x-1/2 w-full max-w-2xl px-4 z-20">
        <form onSubmit={handleSearch} className="relative">
          <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 text-[#2E7D32]/60 w-5 h-5" />
          <input
            type="text"
            placeholder="Search organic products (e.g., apples, rice, honey...)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-14 pr-6 py-4 bg-white/95 backdrop-blur-md rounded-full shadow-2xl focus:ring-4 focus:ring-[#2E7D32]/30 focus:outline-none transition-all text-base text-[#2E7D32] placeholder:text-[#2E7D32]/50"
          />
        </form>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 p-3 md:p-4 bg-[#E8F5E9]/20 hover:bg-[#E8F5E9]/40 backdrop-blur-md border border-[#E8F5E9]/40 rounded-full transition-all duration-300 z-10 group"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6 text-white" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 p-3 md:p-4 bg-[#E8F5E9]/20 hover:bg-[#E8F5E9]/40 backdrop-blur-md border border-[#E8F5E9]/40 rounded-full transition-all duration-300 z-10 group"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6 text-white" />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex gap-3 z-10">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === currentSlide
                ? 'w-12 bg-[#E8F5E9] shadow-lg'
                : 'w-2 bg-white/40 hover:bg-[#E8F5E9]/60'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce hidden md:block z-10">
        <div className="w-6 h-10 border-2 border-[#E8F5E9]/60 rounded-full flex justify-center backdrop-blur-sm">
          <div className="w-1 h-3 bg-[#E8F5E9]/80 rounded-full mt-2"></div>
        </div>
      </div>
    </section>
  );
};

